// @givmo/compliance — the review (CCM-R1-REVIEW-WEB; plan §1.8; D-CCM-51, UX slice 4's frames 91,
// 92, 96–100, 99b, 102, 102b and 106).
//
// What Givmo found when it compared the organization's recorded facts with the rules it evaluates:
// the filings it identified, the questions it needs answered, what the rules don't settle and what
// reads as no filing, each in its own section, and a sticky "Your decisions" card holding the one act.
//
// ══ ONE ACT ON THE REVISION SHOWN ══════════════════════════════════════════════════════════════
//
// Opening the page generates a revision for someone who can make changes in Compliance (nothing is
// written when nothing moved since the newest one); someone who can only view reads the newest one.
// Nothing arrives chosen: each card takes its own choice, and "Save decisions" records every choice
// made as ONE review of the revision shown, its digest echoed. The backend refuses a revision that
// moved since (R1, re-presented here: §1.8 item 4) and a second review of one revision, so after a
// save the page generates again and later choices land on the new revision.
//
// ══ WHAT IS NOT HERE, AND WHY ══════════════════════════════════════════════════════════════════
//
//   • Stopping or restarting a filing from its own panel is the panel's (`CCM-R1-TRACKING-PANEL`):
//     the review finds a stopped filing again (S5), and only its "Add to calendar" restarts it here.
//   • A question no door answers yet (an organization's own answer, a figure, a donor count: G3,
//     `CCM-R1-FACTS`) shows its words, "I don't know" and an upload, and no field: a field that
//     could not save would be a dead control.
//   • Today no rule evaluates (`CCM-R1-ASSESS-RULES`), so every organization sees frame 106's page.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  Field,
  Icon,
  PageHeader,
  Skeleton,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ClipboardList } from 'lucide-react';
import {
  isGivmoError,
  type ComplianceProfileParameterRecord,
  type ComplianceProfileParameterValue,
} from '@givmo/api-client';
import { AddFilingSheet } from './add-filing-sheet';
import { complianceAiSettingQueryKey, decodeComplianceAiSetting } from './compliance-ai-setting';
import { useCompliance } from './compliance-context';
import {
  decodeProfile,
  displayComplianceDate,
  displayLocalDate,
  extractObligations,
  isModuleUnavailable,
  isYearEndChangeUnanswered,
  serverMessage,
  US_JURISDICTIONS,
  type YearEndChange,
} from './compliance-decode';
import { ComplianceDisclosure } from './compliance-disclosure';
import { comparisonOf, decodeRuleChange } from './compliance-rule-change';
import { ruleChangeQueryKey } from './filing-panel-rule-change';
import { recordedFiscalYearEnd } from './compliance-panel-model';
import { localIsoDate } from './compliance-filings-model';
import { parseMonthDay, YearEndChangeQuestion } from './filing-panel-forms';
import {
  complianceProfileQueryKey,
  listAllObligations,
  listAllProposalItems,
  listAllProposalResults,
  listAllReviewAsks,
} from './compliance-queries';
import { COMPLIANCE_PAGE_TEST_IDS, COMPLIANCE_ROUTES } from './compliance-routes';
import { ModuleUnavailableNotice, STICKY_COLUMN_CLASSES } from './compliance-shared';
import { FeeChart } from './filing-workbench-fee-chart';
import { SAVE_FAILED } from './filing-workbench-items';
import { UploadDocumentsSheet } from './upload-sheet';
import { reviewEntryQueryKey } from './review-entry';
import {
  decodeAsk,
  decodeItem,
  decodeProposal,
  decodeResult,
  decodeReview,
  decodeRevision,
  isAskedWithoutAccess,
  ruleKey,
  staleRefusalOf,
  type ReviewAsk,
  type ReviewItem,
  type ReviewResult,
  type ReviewRevision,
  type ItemAsk,
} from './review-decode';
import {
  CHOICE_LABELS,
  buildReviewView,
  decidedChoiceLabel,
  decidedDay,
  itemSignature,
  itemTitlesOf,
  nameIfKnown,
  nameOrSomeone,
  openCount,
  selectionOf,
  staleReasonLines,
  type AnswerControl,
  type ChangeCard,
  type Choice,
  type ChoiceValue,
  type DecidedCard,
  type ExclusionCard,
  type FactLine,
  type FilingCard,
  type StoppedFiling,
  type IssueCard,
  type QuestionCard,
  type ReviewView,
  type Selection,
} from './review-model';
import {
  ActionRow,
  AskDialog,
  type AskRefusal,
  BasisToggle,
  CardTitle,
  ChoiceButtons,
  DateBlock,
  EvidenceNote,
  FactList,
  ItemCard,
  MonthDayPicker,
  QuietLine,
  REVIEW_PREFIX as PREFIX,
  ReviewSection,
  StatesPicker,
  TextAction,
  WhatChanged,
} from './review-parts';
import * as W from './review-words';

// ── What the page holds ────────────────────────────────────────────────────────────────────────

interface Presentation {
  revision: ReviewRevision;
  items: ReviewItem[];
  results: ReviewResult[];
}

/** How the presented revision came to replace the one before it (N33, N34). */
interface Transition {
  previousItemKeys?: ReadonlySet<string>;
  changedItemKeys?: ReadonlySet<string>;
  othersSince?: ReadonlySet<string>;
  /** Choices made before it were kept where their item didn't change. */
  kept: boolean;
}

type Refusal =
  | { kind: 'stale'; detail: string; reasons: string[]; savedBy?: { name: string; time: string } }
  | { kind: 'refused'; title?: string; detail: string };

/** What a recorded fact is answered with, at the profile's own door. */
interface Answer {
  parameterKey: string;
  value: Record<string, unknown>;
}

function timeOf(instant: string | undefined): string {
  if (instant === undefined) return '';
  const date = new Date(instant);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function resultsByKeyOf(results: readonly ReviewResult[]): Map<string, ReviewResult> {
  return new Map(results.map((result) => [ruleKey(result.rule), result]));
}

function defined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

/**
 * The states the organization records soliciting in. The profile records the whole list at once, so a
 * states answer starts from them: it adds a state or corrects one, never replaces the list unseen.
 */
const RecordedStates = createContext<readonly string[]>([]);

/**
 * The states the console offers: the 50 states and the District of Columbia, the only codes the
 * profile's door takes (CCM-R1-FOOTPRINT-CODES-VALID). A code on record outside them, left by an API
 * caller or a connected AI before the door held the list, is never shown, so a states answer neither
 * starts from it nor sends it: the person saves exactly the states they see.
 */
const OFFERED_STATES: ReadonlySet<string> = new Set(US_JURISDICTIONS.map((entry) => entry.code));

/**
 * What else the profile records that shapes an answer: the states list stated complete (D-CCM-72),
 * the fiscal year end and whether moving it asks Y1 (D-CCM-74), and the name those lines carry.
 * `served` is false where the backend predates the statement and the answer, which it would refuse:
 * neither is then offered or sent.
 */
interface RecordedFacts {
  served: boolean;
  statesComplete: boolean;
  yearEnd?: { month: number; day: number };
  yearEndChangeRequired: boolean;
  organization: string;
}
const RecordedFactsContext = createContext<RecordedFacts>({
  served: false,
  statesComplete: false,
  yearEndChangeRequired: false,
  organization: '',
});

/** What the profile records, for the answers it shapes: the states, and the facts beside them. */
function RecordedProfile({
  states,
  facts,
  children,
}: {
  states: readonly string[];
  facts: RecordedFacts;
  children: ReactNode;
}) {
  return (
    <RecordedStates.Provider value={states}>
      <RecordedFactsContext.Provider value={facts}>{children}</RecordedFactsContext.Provider>
    </RecordedStates.Provider>
  );
}

// ── Asking a teammate (CCM-R1-REVIEW-ASSIGN; D-CCM-51, frames 93, 94, 94b) ────────────────────

/**
 * What the page knows of the asks on its items, and the acts on them. Every card reads it; only
 * someone who can make changes is offered the acts. An ask is the item's open one as the items
 * read serves it (to every reader of the review), or as this visit just changed it.
 */
interface Asking {
  askOn: (itemKeys: readonly string[]) => { itemKey: string; ask: ItemAsk } | undefined;
  open: (itemKey: string, title: string) => void;
  withdraw: (itemKey: string, askId: string) => void;
  viewer: boolean;
  viewerUserId?: string;
  nameOf: (userId: string) => string | undefined;
}

const AskingContext = createContext<Asking | undefined>(undefined);

/** An ask's day, in the frames' form ("Nov 12, 2026"), as the viewer's calendar reads the instant. */
function askDay(ask: ItemAsk): string {
  return displayLocalDate(ask.askedAt);
}

/**
 * Who was asked, for anyone but the person asked: N43's "Asked {Name} on {date}" to the asker, and
 * U20 (b)'s "{Asker} asked {Name} on {date}" to everyone else, and wherever who is viewing isn't
 * known yet (it's true for anyone).
 */
function askedLine(
  ask: ItemAsk,
  viewerUserId: string | undefined,
  nameOf: (userId: string) => string | undefined,
): string {
  const name = nameIfKnown(nameOf, ask.askedUserId);
  // CCM-R1-REVIEW-ASK-VIA-AI: an ask a connected AI made reads as made through the asker's AI, to the
  // asker and everyone else alike.
  if (ask.channel === 'connector')
    return W.askedThroughAi(name, nameIfKnown(nameOf, ask.askedByUserId), askDay(ask));
  return viewerUserId !== undefined && ask.askedByUserId === viewerUserId
    ? W.askedOn(name, askDay(ask))
    : W.someoneAsked(nameOrSomeone(nameOf, ask.askedByUserId), name, askDay(ask));
}

/**
 * The person asked's line on the item's card: frame 94b's, its colon a period with no note (U19).
 * An ask a connected AI made reads as made through the asker's AI (CCM-R1-REVIEW-ASK-VIA-AI).
 */
function askedYouLine(ask: ItemAsk, nameOf: (userId: string) => string | undefined): string {
  if (ask.channel === 'connector')
    return (
      ask.note !== undefined ? W.askedYouAboutThisThroughAi : W.askedYouAboutThisThroughAiNoNote
    )(nameIfKnown(nameOf, ask.askedByUserId), askDay(ask));
  return (ask.note !== undefined ? W.askedYouAboutThis : W.askedYouAboutThisNoNote)(
    nameOrSomeone(nameOf, ask.askedByUserId),
    askDay(ask),
  );
}

/**
 * The person asked's line on U23, naming the item: U23's, its period a colon before a note (N-U23);
 * through the asker's AI where a connected AI made the ask (CCM-R1-REVIEW-ASK-VIA-AI).
 */
function askedYouAboutItemLine(
  ask: ItemAsk,
  title: string,
  nameOf: (userId: string) => string | undefined,
): string {
  if (ask.channel === 'connector')
    return (
      ask.note !== undefined ? W.askedYouAboutItemThroughAi : W.askedYouAboutItemThroughAiNoNote
    )(nameIfKnown(nameOf, ask.askedByUserId), title, askDay(ask));
  return (ask.note !== undefined ? W.askedYouAboutItemWithNote : W.askedYouAboutItem)(
    nameOrSomeone(nameOf, ask.askedByUserId),
    title,
    askDay(ask),
  );
}

/**
 * A card's ask, as ONE quiet-line part:
 * - N42's "Ask a teammate" on an open item;
 * - once asked, N43's "Asked {Name} on {date} · Change · Withdraw", one phrase with its own inline
 *   separators, as frame 94 sets it (the card's quiet line puts its wider one only before it).
 *   Anyone else who can make changes reads who asked, "{Asker} asked {Name} on {date} · Change ·
 *   Withdraw" (U20 (b)); someone who can only view reads that line alone (U21).
 * None for the person asked, whose block is frame 94b's, and none for an unasked item someone can
 * only view. Until the host knows who is viewing (its own read of the person), nothing is offered:
 * the asked line alone, and no "Ask a teammate", since the person asked could otherwise be offered
 * Withdraw, or themselves to ask.
 */
function useAskParts(itemKeys: readonly string[], title: string): ReactNode[] {
  const asking = useContext(AskingContext);
  const first = itemKeys[0];
  if (asking === undefined || first === undefined) return [];
  const found = asking.askOn(itemKeys);
  const acts = !asking.viewer && asking.viewerUserId !== undefined;
  if (found === undefined) {
    return acts
      ? [
          <TextAction
            key="ask"
            onClick={() => asking.open(first, title)}
            testId={`${PREFIX}-ask-${first}`}
          >
            {W.ASK_A_TEAMMATE}
          </TextAction>,
        ]
      : [];
  }
  if (asking.viewerUserId !== undefined && found.ask.askedUserId === asking.viewerUserId) return [];
  const asked = askedLine(found.ask, asking.viewerUserId, asking.nameOf);
  if (!acts)
    return [
      <span key="asked" {...testIdProp(`${PREFIX}-asked-${found.itemKey}`)}>
        {asked}
      </span>,
    ];
  return [
    <span key="asked">
      <span {...testIdProp(`${PREFIX}-asked-${found.itemKey}`)}>{asked}</span>
      {' · '}
      <TextAction
        onClick={() => asking.open(found.itemKey, title)}
        testId={`${PREFIX}-ask-change-${found.itemKey}`}
      >
        {W.CHANGE}
      </TextAction>
      {' · '}
      <TextAction
        onClick={() => asking.withdraw(found.itemKey, found.ask.askId)}
        testId={`${PREFIX}-ask-withdraw-${found.itemKey}`}
      >
        {W.WITHDRAW}
      </TextAction>
    </span>,
  ];
}

/**
 * A card's ask where it stands on its own, beside the choices or under the card. Who was asked is a
 * quiet line; a lone "Ask a teammate" sits on the card's own line, where the approved frames put it
 * (101, 102, 105: on the quiet line's smaller one it sat 2 px high).
 */
function AskAside({ itemKeys, parts }: { itemKeys: readonly string[]; parts: ReactNode[] }) {
  const asking = useContext(AskingContext);
  if (parts.length === 0) return null;
  return asking?.askOn(itemKeys) === undefined ? (
    <div>{parts}</div>
  ) : (
    <QuietLine>{parts}</QuietLine>
  );
}

/** Frame 94b: the person asked reads the ask under the item's title, with the asker's note. */
function AskedYou({ itemKeys }: { itemKeys: readonly string[] }) {
  const asking = useContext(AskingContext);
  const found = asking?.askOn(itemKeys);
  if (
    asking === undefined ||
    found === undefined ||
    asking.viewerUserId === undefined ||
    found.ask.askedUserId !== asking.viewerUserId
  )
    return null;
  return (
    <div
      className="flex flex-col gap-4 border-l-2 border-brand-600 pl-12"
      {...testIdProp(`${PREFIX}-asked-you`)}
    >
      <p className="text-s13 font-w600 leading-h15 text-text-strong">
        {askedYouLine(found.ask, asking.nameOf)}
      </p>
      {found.ask.note !== undefined && (
        <p className="text-s13 leading-h15 text-text">“{found.ask.note}”</p>
      )}
    </div>
  );
}

function sameMembers(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && b.every((code) => a.includes(code));
}

// ── The page ───────────────────────────────────────────────────────────────────────────────────

export function ReviewPage() {
  const {
    orgRef,
    isReady,
    operations,
    moduleStanding,
    canWrite,
    personName,
    organizationName,
    viewerUserId,
    complianceWriters,
    inviteToCompliance,
  } = useCompliance();
  const live = isReady && moduleStanding === 'available' && orgRef !== null;
  const writer = canWrite === true;
  const ref = orgRef ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const nameOf = (userId: string) => personName?.(userId);

  const [shown, setShown] = useState<Presentation | undefined>();
  // `none`: someone who can only view, and no revision made yet: nothing to read.
  const [status, setStatus] = useState<'loading' | 'ready' | 'none'>('loading');
  const [failed, setFailed] = useState<unknown>();
  const [transition, setTransition] = useState<Transition>({ kept: false });
  const [choices, setChoices] = useState<Map<string, Choice>>(new Map());
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [saved, setSaved] = useState<{ decided: number } | undefined>();
  const [answered, setAnswered] = useState(false);
  const [reopened, setReopened] = useState<ReadonlySet<string>>(new Set());
  const [addingFor, setAddingFor] = useState<
    { jurisdictionCode: string; ruleId: string } | 'any'
  >();
  const [uploadingFor, setUploadingFor] = useState<string | undefined>();
  const [uploads, setUploads] = useState<ReadonlyMap<string, string>>(new Map());
  // The decisions this person saved in this visit: the next revision shows them as "Decided".
  const mySaved = useRef<ReadonlySet<string>>(new Set());

  // The reads the cards word their facts and dates with: the calendar's filings, the profile, the
  // AI setting (the upload line). Each under the key every other page reads it by.
  const obligations = useQuery({
    queryKey: ['compliance', 'obligations', orgRef],
    queryFn: () => listAllObligations(operations, ref),
    enabled: live,
  });
  const profile = useQuery({
    queryKey: complianceProfileQueryKey(orgRef),
    queryFn: () => operations.getProfile(ref),
    enabled: live,
  });
  const aiSetting = useQuery({
    queryKey: complianceAiSettingQueryKey(orgRef),
    queryFn: () => operations.getAiSetting(ref),
    enabled: live,
  });
  const aiOff = decodeComplianceAiSetting(aiSetting.data)?.givmoAiEnabled === false;

  const decodedProfile = profile.data !== undefined ? decodeProfile(profile.data) : undefined;
  const fiscalYearEnd = recordedFiscalYearEnd(decodedProfile);
  const toldJurisdictions = useMemo(() => {
    const recorded = decodedProfile?.parameters.find(
      (parameter) =>
        parameter.parameterKey === 'solicitation_jurisdictions' &&
        parameter.status !== 'superseded',
    )?.recordedValue;
    return new Set(
      (recorded ?? '')
        .split(',')
        .map((code) => code.trim())
        .filter((code) => OFFERED_STATES.has(code)),
    );
  }, [decodedProfile]);
  const recordedStates = useMemo(() => [...toldJurisdictions], [toldJurisdictions]);
  const recordedFacts = useMemo<RecordedFacts>(() => {
    const inForce = (key: string) =>
      decodedProfile?.parameters.find(
        (parameter) => parameter.parameterKey === key && parameter.status !== 'superseded',
      );
    const yearEnd = parseMonthDay(inForce('fiscal_year_end')?.recordedValue);
    return {
      served: decodedProfile?.yearEndChangeRequired !== undefined,
      statesComplete: inForce('solicitation_jurisdictions')?.listComplete === true,
      ...(yearEnd !== undefined ? { yearEnd } : {}),
      yearEndChangeRequired: decodedProfile?.yearEndChangeRequired === true,
      organization: organizationName ?? '',
    };
  }, [decodedProfile, organizationName]);
  const calendar = useMemo(() => {
    const map = new Map<string, { ruleKey: string; ruleChangeFlaggedOn?: string }>();
    for (const obligation of extractObligations(obligations.data)) {
      // H-c: a filing whose tracking stopped is off the calendar: the review offers it again.
      if (obligation.candidateState !== 'confirmed' || obligation.trackingStoppedAt !== undefined)
        continue;
      map.set(obligation.obligationId, {
        ruleKey: `${obligation.jurisdictionCode}:${obligation.ruleId}`,
        ...(obligation.ruleChangeFlaggedOn !== undefined
          ? { ruleChangeFlaggedOn: obligation.ruleChangeFlaggedOn }
          : {}),
      });
    }
    return map;
  }, [obligations.data]);
  // D-CCM-58 (U16): the filings whose tracking stopped, off the calendar: the review offers each
  // again, saying so (S5), and "Add all" leaves it out.
  const stopped = useMemo(() => {
    const map = new Map<string, StoppedFiling>();
    for (const obligation of extractObligations(obligations.data)) {
      if (obligation.candidateState !== 'confirmed' || obligation.trackingStoppedAt === undefined)
        continue;
      map.set(obligation.obligationId, {
        stoppedAt: obligation.trackingStoppedAt,
        ...(obligation.trackingStoppedByUserId !== undefined
          ? { stoppedByUserId: obligation.trackingStoppedByUserId }
          : {}),
        ...(obligation.trackingStoppedChannel !== undefined
          ? { stoppedChannel: obligation.trackingStoppedChannel }
          : {}),
      });
    }
    return map;
  }, [obligations.data]);

  // ── Reading and presenting a revision ──

  /** The revision to present: generated for someone who can make changes, else the newest. */
  const freshRevision = async (): Promise<ReviewRevision | undefined> => {
    if (writer) return decodeRevision(await operations.generateProposalRevision(ref));
    try {
      return decodeProposal(await operations.getProposal(ref));
    } catch (error) {
      // Someone who can only view makes no revision: none yet is nothing to read, not a failure.
      if (isGivmoError(error) && error.status === 404) return undefined;
      throw error;
    }
  };

  const readPresentation = async (revision: ReviewRevision): Promise<Presentation> => {
    const [itemRows, resultRows] = await Promise.all([
      listAllProposalItems(operations, ref, revision.revisionId),
      listAllProposalResults(operations, ref, revision.revisionId),
    ]);
    return {
      revision,
      items: itemRows.map(decodeItem).filter(defined),
      results: resultRows.map(decodeResult).filter(defined),
    };
  };

  /**
   * Present the newest revision. After this person's own save it simply replaces the one shown;
   * otherwise (an answer, "Show the updated review") it is re-presented: the items new or changed
   * since the screen before are marked, another person's decisions are named, and a choice already
   * made is kept where its card didn't change (D9).
   */
  const present = async (after: 'open' | 'own-save' | 're-present') => {
    const before = shown;
    const revision = await freshRevision();
    if (revision === undefined) {
      setShown(undefined);
      setStatus('none');
      return undefined;
    }
    const next = await readPresentation(revision);
    setStatus('ready');
    void queryClient.invalidateQueries({ queryKey: reviewEntryQueryKey(orgRef) });
    // A revision that moved may have moved with the calendar (another person's additions): the
    // filings it links to are read again, so "Already on your calendar" names them.
    if (after === 're-present') {
      void queryClient.invalidateQueries({ queryKey: ['compliance', 'obligations', orgRef] });
    }
    if (after !== 're-present' || before === undefined) {
      setTransition({ kept: false });
      setShown(next);
      return next;
    }
    const beforeResults = resultsByKeyOf(before.results);
    const nextResults = resultsByKeyOf(next.results);
    const signatures = new Map(
      before.items.map((item) => [item.itemKey, itemSignature(item, beforeResults)]),
    );
    const decisionIds = new Set(
      before.items.map((item) => item.decision?.decisionId).filter(defined),
    );
    const changedItemKeys = new Set(
      next.items
        .filter(
          (item) =>
            signatures.has(item.itemKey) &&
            signatures.get(item.itemKey) !== itemSignature(item, nextResults),
        )
        .map((item) => item.itemKey),
    );
    const previousItemKeys = new Set(before.items.map((item) => item.itemKey));
    const othersSince = new Set(
      next.items
        .filter(
          (item) =>
            item.decision !== undefined &&
            !decisionIds.has(item.decision.decisionId) &&
            !mySaved.current.has(item.decision.decisionId),
        )
        .map((item) => item.itemKey),
    );
    // Keep a choice whose card is still there, holding only items that neither moved nor are new.
    const nextView = buildReviewView({
      revision: next.revision,
      items: next.items,
      results: next.results,
      orgName: organizationName ?? '',
      personName: nameOf,
      calendar,
      stopped,
    });
    const cardItems = new Map<string, string[]>();
    for (const card of nextView.changes) cardItems.set(card.itemKey, [card.itemKey]);
    for (const card of nextView.filings) cardItems.set(card.itemKey, [card.itemKey]);
    for (const card of [...nextView.noFiling.shown, ...nextView.noFiling.folded]) {
      cardItems.set(card.itemKey, [card.itemKey]);
    }
    for (const card of [...nextView.questions, ...nextView.issues])
      cardItems.set(card.key, card.itemKeys);
    const keptChoices = new Map<string, Choice>();
    for (const [key, choice] of choices) {
      const itemKeys = cardItems.get(key);
      if (
        itemKeys !== undefined &&
        itemKeys.every((itemKey) => previousItemKeys.has(itemKey) && !changedItemKeys.has(itemKey))
      ) {
        keptChoices.set(key, choice);
      }
    }
    setChoices(keptChoices);
    setTransition({
      previousItemKeys,
      changedItemKeys,
      othersSince,
      kept: keptChoices.size > 0,
    });
    setShown(next);
    return next;
  };

  useEffect(() => {
    if (!live) return;
    let cancelled = false;
    setFailed(undefined);
    present('open').catch((error: unknown) => {
      if (!cancelled) setFailed(error);
    });
    return () => {
      cancelled = true;
    };
    // Opened once per organization and standing; later presentations are this page's own acts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, orgRef, writer]);

  const view: ReviewView | undefined = useMemo(
    () =>
      shown !== undefined
        ? buildReviewView({
            revision: shown.revision,
            items: shown.items.map((item) =>
              reopened.has(item.itemKey) && item.decision !== undefined
                ? { ...item, decision: { ...item.decision, revisionId: shown.revision.revisionId } }
                : item,
            ),
            results: shown.results,
            orgName: organizationName ?? '',
            personName: nameOf,
            calendar,
            stopped,
            fiscalYearEnd,
            toldJurisdictions,
            ...(transition.previousItemKeys !== undefined
              ? { previousItemKeys: transition.previousItemKeys }
              : {}),
            ...(transition.changedItemKeys !== undefined
              ? { changedItemKeys: transition.changedItemKeys }
              : {}),
            ...(transition.othersSince !== undefined
              ? { othersSince: transition.othersSince }
              : {}),
          })
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      shown,
      transition,
      calendar,
      stopped,
      fiscalYearEnd,
      toldJurisdictions,
      reopened,
      organizationName,
    ],
  );
  const selection = view !== undefined ? selectionOf(choices, view) : undefined;

  const choose = (key: string, value: ChoiceValue | undefined, reason?: string) => {
    setSaved(undefined);
    setAnswered(false);
    setChoices((current) => {
      const next = new Map(current);
      if (value === undefined) next.delete(key);
      else next.set(key, { value, ...(reason !== undefined ? { reason } : {}) });
      return next;
    });
  };

  // ── The act ──

  const save = async () => {
    if (shown === undefined || selection === undefined || selection.decisions.length === 0) return;
    setSaving(true);
    setRefusal(undefined);
    try {
      const recorded = decodeReview(
        await operations.recordProposalReview(ref, shown.revision.revisionId, {
          revision_sha256: shown.revision.payloadSha256,
          decisions: selection.decisions,
        }),
      );
      // The decisions just recorded are this visit's own: never named as someone else's (N34).
      mySaved.current = new Set([
        ...mySaved.current,
        ...(recorded?.decisions.map((decision) => decision.decisionId) ?? []),
      ]);
      setSaved({ decided: selection.chosen.length });
      setChoices(new Map());
      setReopened(new Set());
      void queryClient.invalidateQueries({ queryKey: ['compliance', 'obligations', orgRef] });
      await present('own-save');
    } catch (error) {
      const stale = staleRefusalOf(error);
      if (stale !== undefined) {
        let savedBy: { name: string; time: string } | undefined;
        if (stale.reasons.includes('reviewed')) {
          try {
            const review = decodeReview(
              await operations.getProposalReview(ref, shown.revision.revisionId),
            );
            if (review !== undefined) {
              savedBy = {
                name: nameOrSomeone(nameOf, review.committedByUserId),
                time: timeOf(review.committedAt),
              };
            }
          } catch {
            // The reason line is the review's to name; without it, R1 stands alone.
          }
        }
        setRefusal({
          kind: 'stale',
          detail: stale.detail,
          reasons: stale.reasons,
          ...(savedBy ? { savedBy } : {}),
        });
      } else if (isGivmoError(error)) {
        setRefusal({ kind: 'refused', title: error.entries[0]?.title, detail: error.message });
      } else {
        setRefusal({ kind: 'refused', detail: SAVE_FAILED });
      }
    } finally {
      setSaving(false);
    }
  };

  const showUpdated = async () => {
    setRefusal(undefined);
    try {
      await present('re-present');
    } catch (error) {
      setFailed(error);
    }
  };

  /** An answer at the profile's own door; the review is re-presented with it (N23). */
  const answer = async ({ parameterKey, value }: Answer) => {
    try {
      await operations.recordProfileParameter(ref, {
        // The key is the served `answer_parameter_key` (or a fact's own door), one the profile records.
        parameter_key: parameterKey as ComplianceProfileParameterRecord['parameter_key'],
        value: value as ComplianceProfileParameterValue,
      });
      await queryClient.invalidateQueries({ queryKey: complianceProfileQueryKey(orgRef) });
      await present('re-present');
      setAnswered(true);
      return true;
    } catch (error) {
      // CCM-BE24-B (cut 1008-42's review, F4): a year end refused for want of the organization's
      // answer re-reads the profile, which then asks Y1 under the field; Y7 is never shown.
      if (isYearEndChangeUnanswered(error)) {
        await queryClient.invalidateQueries({ queryKey: complianceProfileQueryKey(orgRef) });
        return false;
      }
      toast.error(serverMessage(error, SAVE_FAILED));
      return false;
    }
  };

  // ── Asking a teammate (CCM-R1-REVIEW-ASSIGN) ──

  // What this visit changed, for the revision shown: an ask made (or changed), or withdrawn (null).
  const [askChanges, setAskChanges] = useState<{
    revisionId?: string;
    asks: ReadonlyMap<string, ItemAsk | null>;
  }>({ asks: new Map() });
  const [askingFor, setAskingFor] = useState<
    { itemKey: string; title: string; person?: string; note?: string } | undefined
  >();
  const [askPending, setAskPending] = useState(false);
  const [askRefusal, setAskRefusal] = useState<AskRefusal | undefined>();
  const shownRevisionId = shown?.revision.revisionId;
  // The open asks, as the asks list serves them: U23's rows below. The cards never wait for it: an
  // item's own ask says how it was made (CCM-R1-REVIEW-ASK-CHANNEL-ON-ITEM), so an ask a connected
  // AI made reads as one from the first paint.
  const openAsks = useQuery({
    queryKey: ['compliance', 'review', 'asks', orgRef, 'open'],
    queryFn: () => listAllReviewAsks(operations, ref, { state: 'open' }),
    enabled: live,
  });
  const servedAsks = useMemo(
    () =>
      new Map(
        (shown?.items ?? []).flatMap((item) =>
          item.ask !== undefined ? [[item.itemKey, item.ask] as const] : [],
        ),
      ),
    [shown],
  );
  const changedAsks =
    askChanges.revisionId === shownRevisionId ? askChanges.asks : new Map<string, ItemAsk | null>();
  const askOn = (itemKeys: readonly string[]) => {
    for (const itemKey of itemKeys) {
      const changed = changedAsks.get(itemKey);
      const ask = changedAsks.has(itemKey) ? (changed ?? undefined) : servedAsks.get(itemKey);
      if (ask !== undefined) return { itemKey, ask };
    }
    return undefined;
  };
  const changeAsk = (itemKey: string, ask: ItemAsk | null) =>
    setAskChanges((current) => ({
      revisionId: shownRevisionId,
      asks: new Map(current.revisionId === shownRevisionId ? current.asks : []).set(itemKey, ask),
    }));
  const openAsk = (itemKey: string, title: string) => {
    // "Change" opens the dialog filled in with the ask as it stands.
    const current = askOn([itemKey])?.ask;
    setAskRefusal(undefined);
    setAskingFor({
      itemKey,
      title,
      ...(current !== undefined ? { person: current.askedUserId } : {}),
      ...(current?.note !== undefined ? { note: current.note } : {}),
    });
  };
  const askTeammate = async (askedUserId: string, note: string) => {
    if (shown === undefined || askingFor === undefined) return;
    setAskPending(true);
    setAskRefusal(undefined);
    try {
      const asked = decodeAsk(
        await operations.askAboutReviewItem(ref, {
          revision_id: shown.revision.revisionId,
          item_key: askingFor.itemKey,
          asked_user_id: askedUserId,
          ...(note.trim().length > 0 ? { note: note.trim() } : {}),
        }),
      );
      if (asked !== undefined) changeAsk(askingFor.itemKey, asked);
      setAskingFor(undefined);
      void queryClient.invalidateQueries({ queryKey: ['compliance', 'review', 'asks', orgRef] });
    } catch (error) {
      // R2, N42's line or R4, as served; the dialog stays open.
      setAskRefusal({
        line: serverMessage(error, SAVE_FAILED),
        withoutAccess: isAskedWithoutAccess(error),
      });
    } finally {
      setAskPending(false);
    }
  };
  const withdrawAsk = async (itemKey: string, askId: string) => {
    try {
      await operations.withdrawReviewAsk(ref, askId);
      changeAsk(itemKey, null);
      void queryClient.invalidateQueries({ queryKey: ['compliance', 'review', 'asks', orgRef] });
    } catch (error) {
      toast.error(serverMessage(error, SAVE_FAILED));
    }
  };
  const asking: Asking = {
    askOn,
    open: openAsk,
    withdraw: (itemKey, askId) => void withdrawAsk(itemKey, askId),
    viewer: !writer,
    ...(viewerUserId !== undefined ? { viewerUserId } : {}),
    nameOf,
  };
  // Who may be asked: people who can make changes in Compliance, never the asker.
  const teammates = (complianceWriters ?? []).filter((person) => person.userId !== viewerUserId);

  // U23 (D-CCM-60): the open asks on items the review doesn't show now, last on the page, each
  // titled from the revision it was asked in (one items-and-results read per earlier revision; a
  // revision never changes). be#2811's CHANGE-SETTLED reads an ask on a decided change resolved, so
  // none of these waits on a decision already made.
  const notInReview = useMemo(() => {
    if (view === undefined) return [];
    // Every item the page shows with its ask, as a card or a decision: an ask on any other one (an
    // item the revision doesn't hold, or one it holds that no card shows, such as a filing or a
    // no-filing beside its rule's change card, or a filing already on the calendar, whose line draws
    // no ask) is listed here, or no one could find it.
    const shownKeys = new Set([
      ...view.changes.map((card) => card.itemKey),
      ...view.filings.map((card) => card.itemKey),
      ...view.noFiling.shown.map((card) => card.itemKey),
      ...view.noFiling.folded.map((card) => card.itemKey),
      ...view.decided.map((card) => card.itemKey),
      ...view.questions.flatMap((card) => card.itemKeys),
      ...view.issues.flatMap((card) => card.itemKeys),
    ]);
    return (openAsks.data ?? [])
      .map(decodeAsk)
      .filter(
        (ask): ask is ReviewAsk =>
          ask !== undefined && ask.state === 'open' && !shownKeys.has(ask.itemKey),
      );
  }, [openAsks.data, view]);
  // The revision shown is titled from what the page already read; each earlier one is read once.
  const askRevisions = [...new Set(notInReview.map((ask) => ask.revisionId))].filter(
    (revisionId) => revisionId !== shownRevisionId,
  );
  const askRevisionReads = useQueries({
    queries: askRevisions.map((revisionId) => ({
      queryKey: ['compliance', 'review', 'revision', orgRef, revisionId],
      queryFn: async () => {
        const [itemRows, resultRows] = await Promise.all([
          listAllProposalItems(operations, ref, revisionId),
          listAllProposalResults(operations, ref, revisionId),
        ]);
        return {
          items: itemRows.map(decodeItem).filter(defined),
          results: resultRows.map(decodeResult).filter(defined),
        };
      },
      enabled: live,
      staleTime: Infinity,
    })),
  });
  // Each revision's titles, once: a row waits for its revision's read, and a row whose item has no
  // title there isn't drawn.
  const titlesByRevision = new Map<string, Map<string, string>>();
  if (shown !== undefined && notInReview.some((ask) => ask.revisionId === shownRevisionId)) {
    titlesByRevision.set(
      shown.revision.revisionId,
      itemTitlesOf({
        revision: shown.revision,
        items: shown.items,
        results: shown.results,
        orgName: organizationName ?? '',
        personName: nameOf,
        calendar,
      }),
    );
  }
  askRevisions.forEach((revisionId, index) => {
    const read = askRevisionReads[index]?.data;
    if (shown === undefined || read === undefined) return;
    titlesByRevision.set(
      revisionId,
      itemTitlesOf({
        revision: { ...shown.revision, revisionId },
        items: read.items,
        results: read.results,
        orgName: organizationName ?? '',
        personName: nameOf,
        calendar,
      }),
    );
  });
  const notInReviewRows = notInReview.flatMap((ask) => {
    const title = titlesByRevision.get(ask.revisionId)?.get(ask.itemKey);
    return title !== undefined ? [{ ask, title }] : [];
  });

  const unavailable = failed !== undefined && isModuleUnavailable(failed);

  return (
    <RecordedProfile states={recordedStates} facts={recordedFacts}>
      <AskingContext.Provider value={asking}>
        <div
          className="flex w-full max-w-[1120px] flex-col gap-24 px-16 py-28 sm:px-24 lg:px-32"
          {...testIdProp(COMPLIANCE_PAGE_TEST_IDS.review)}
        >
          <PageHeader
            title={W.REVIEW_TITLE}
            {...(view !== undefined && view.anythingEvaluates && shown !== undefined
              ? {
                  subtitle: W.reviewLead(
                    view.evaluatedRuleCount,
                    displayComplianceDate(shown.revision.asOf),
                  ),
                }
              : {})}
          />
          {unavailable ? (
            <ModuleUnavailableNotice testId={`${PREFIX}-unavailable`} />
          ) : failed !== undefined ? (
            <EmptyState
              testId={`${PREFIX}-error`}
              icon={<Icon icon={ClipboardList} size={20} aria-hidden />}
              title={W.REVIEW_TITLE}
              description={serverMessage(failed, 'Please try again.')}
            />
          ) : status === 'none' ? (
            // Someone who can only view, and no revision yet: nothing was compared, so nothing is
            // claimed about what Givmo evaluates (N27 would say so untrue). L4 says why the page is
            // empty and what changes that (D-CCM-71).
            <p
              className="max-w-[720px] text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-viewer-none`)}
            >
              {W.REVIEW_NOT_MADE_YET}
            </p>
          ) : !live || view === undefined || status === 'loading' ? (
            <Skeleton className="h-[480px] w-full" />
          ) : !view.anythingEvaluates && shown !== undefined && shown.items.length === 0 ? (
            <NothingEvaluates
              count={view.notSupported.count}
              titles={view.notSupported.titles}
              inPlacesTold={view.notSupported.inPlacesTold}
              canAdd={writer}
              onAdd={() => setAddingFor('any')}
            />
          ) : (
            <div className="grid items-start gap-32 lg:grid-cols-[minmax(0,1fr)_360px]">
              <div className="flex min-w-0 flex-col gap-32">
                {view.changes.length > 0 && (
                  <ReviewSection title={W.SECTION_CHANGES} testId={`${PREFIX}-changes`}>
                    {view.changes.map((card) => (
                      <ChangeItem
                        key={card.itemKey}
                        card={card}
                        choice={choices.get(card.itemKey)?.value}
                        onChoose={(value) => choose(card.itemKey, value)}
                        viewer={!writer}
                      />
                    ))}
                  </ReviewSection>
                )}
                {(view.filings.length > 0 || view.onCalendar.length > 0) && (
                  <FilingsSection
                    view={view}
                    choices={choices}
                    onChoose={choose}
                    viewer={!writer}
                    nameOf={nameOf}
                    onAnswer={answer}
                    onOpen={(obligationId) =>
                      void navigate({ to: COMPLIANCE_ROUTES.filing(obligationId) })
                    }
                  />
                )}
                {view.questions.length > 0 && (
                  <ReviewSection title={W.SECTION_QUESTIONS} testId={`${PREFIX}-questions`}>
                    {view.questions.map((card) => (
                      <QuestionItem
                        key={card.key}
                        card={card}
                        choice={choices.get(card.key)?.value}
                        onChoose={(value) => choose(card.key, value)}
                        viewer={!writer}
                        nameOf={nameOf}
                        onAnswer={answer}
                        upload={uploads.get(card.key)}
                        aiOff={aiOff}
                        onUpload={() => setUploadingFor(card.key)}
                      />
                    ))}
                  </ReviewSection>
                )}
                {view.issues.length > 0 && (
                  <ReviewSection title={W.SECTION_ISSUES} testId={`${PREFIX}-issues`}>
                    {view.issues.map((card) => (
                      <IssueItem
                        key={card.key}
                        card={card}
                        choice={choices.get(card.key)?.value}
                        onChoose={(value) => choose(card.key, value)}
                        viewer={!writer}
                      />
                    ))}
                  </ReviewSection>
                )}
                {(view.noFiling.shown.length > 0 || view.noFiling.folded.length > 0) && (
                  <NoFilingSection
                    view={view}
                    choices={choices}
                    onChoose={choose}
                    viewer={!writer}
                    calendarRules={new Set([...calendar.values()].map((entry) => entry.ruleKey))}
                    onAddFiling={(card) => setAddingFor(card.ref)}
                    onAnswer={answer}
                  />
                )}
                {view.decided.length > 0 && (
                  <DecidedSection
                    cards={view.decided}
                    justSaved={saved !== undefined}
                    nameOf={nameOf}
                    viewer={!writer}
                    onChange={(card) =>
                      setReopened((current) => new Set([...current, card.itemKey]))
                    }
                    onAddFiling={(card) => card.ref !== undefined && setAddingFor(card.ref)}
                  />
                )}
                <NotSupportedSection
                  count={view.notSupported.count}
                  titles={view.notSupported.titles}
                  inPlacesTold={view.notSupported.inPlacesTold}
                />
                {notInReviewRows.length > 0 && (
                  <NotInReviewSection
                    rows={notInReviewRows}
                    acts={writer && viewerUserId !== undefined}
                    {...(viewerUserId !== undefined ? { viewerUserId } : {})}
                    nameOf={nameOf}
                    onWithdraw={(ask) => void withdrawAsk(ask.itemKey, ask.askId)}
                  />
                )}
              </div>
              <div className={cn('flex min-w-0 flex-col gap-16', STICKY_COLUMN_CLASSES)}>
                <DecisionsCard
                  selection={selection}
                  saving={saving}
                  saved={
                    saved !== undefined
                      ? { decided: saved.decided, open: openCount(view) }
                      : undefined
                  }
                  refusal={refusal}
                  kept={transition.kept && refusal === undefined}
                  answered={answered}
                  viewer={!writer}
                  onSave={() => void save()}
                  onShowUpdated={() => void showUpdated()}
                />
              </div>
            </div>
          )}
          {writer && addingFor !== undefined && (
            <AddFilingSheet
              open
              onClose={() => setAddingFor(undefined)}
              onAdded={() => {
                setAddingFor(undefined);
                void queryClient.invalidateQueries({
                  queryKey: ['compliance', 'obligations', orgRef],
                });
              }}
              {...(addingFor !== 'any'
                ? { preset: { ...addingFor, label: W.ADDED_BY_YOUR_ORGANIZATION } }
                : {})}
            />
          )}
          {writer && (
            <UploadDocumentsSheet
              open={uploadingFor !== undefined}
              onClose={() => setUploadingFor(undefined)}
              onUploaded={(fileName) => {
                const key = uploadingFor;
                if (key !== undefined) setUploads((current) => new Map(current).set(key, fileName));
              }}
            />
          )}
          {writer && askingFor !== undefined && (
            <AskDialog
              key={askingFor.itemKey}
              open
              itemTitle={askingFor.title}
              people={teammates}
              {...(askingFor.person !== undefined ? { initialPerson: askingFor.person } : {})}
              {...(askingFor.note !== undefined ? { initialNote: askingFor.note } : {})}
              pending={askPending}
              {...(askRefusal !== undefined ? { refusal: askRefusal } : {})}
              onAsk={(userId, note) => void askTeammate(userId, note)}
              onClose={() => setAskingFor(undefined)}
              {...(inviteToCompliance !== undefined ? { invite: inviteToCompliance } : {})}
            />
          )}
        </div>
      </AskingContext.Provider>
    </RecordedProfile>
  );
}

// ── Today, before anything evaluates (frame 106) ───────────────────────────────────────────────

function NothingEvaluates({
  count,
  titles,
  inPlacesTold,
  canAdd,
  onAdd,
}: {
  count: number;
  titles: readonly string[];
  inPlacesTold: readonly string[];
  canAdd: boolean;
  onAdd: () => void;
}) {
  return (
    <div
      className="flex max-w-[720px] flex-col gap-24"
      {...testIdProp(`${PREFIX}-nothing-evaluates`)}
    >
      <Card className="flex flex-col items-start gap-12 px-20 py-20">
        <Icon icon={ClipboardList} size={20} className="text-icon-muted" aria-hidden />
        <p className="text-s14 leading-h15 text-text">{W.NOTHING_EVALUATES}</p>
        {canAdd && (
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            onClick={onAdd}
            testId={`${PREFIX}-add-filing`}
          >
            {W.ADD_A_FILING}
          </Button>
        )}
      </Card>
      <NotSupportedSection count={count} titles={titles} inPlacesTold={inPlacesTold} />
    </div>
  );
}

// ── The sections ───────────────────────────────────────────────────────────────────────────────

/**
 * A filing on the calendar that reads otherwise (CCM-R1-REVIEW-CHANGES; frames 101, 102, 105):
 * - its rule updated: N36, then "See what changed" opens N37's comparison with "Use the updated
 *   rule" / "Later", saved with the other choices;
 * - now no filing: N40 and N24, then "Keep tracking it" / "Stop tracking it", and stopping says its
 *   history stays.
 * Where approved words can't say all of what changed (a dated change whose date isn't served, or a
 * rule with no current version), N36 stands alone, with no choice (REVIEW-24's U1). Someone who can
 * only view reads it all, with no choice.
 */
function ChangeItem({
  card,
  choice,
  onChoose,
  viewer,
}: {
  card: ChangeCard;
  choice: ChoiceValue | undefined;
  onChoose: (value: ChoiceValue | undefined) => void;
  viewer: boolean;
}) {
  const { orgRef, operations } = useCompliance();
  const [open, setOpen] = useState(false);
  // The comparison, where the rule was updated: the panel's read, under its key (be#2770).
  const read = useQuery({
    queryKey: ruleChangeQueryKey(orgRef, card.obligationId),
    queryFn: () => operations.getObligationRuleChange(orgRef ?? '', card.obligationId),
    enabled: orgRef !== null && card.updatedOn !== undefined,
  });
  const comparison = comparisonOf(decodeRuleChange(read.data));
  const askParts = useAskParts([card.itemKey], card.title);
  const opened = open && comparison !== undefined;
  const updated =
    card.updatedOn !== undefined ? (
      <p className="text-s13 leading-h15 text-text">
        {W.thisRuleUpdatedOn(card.updatedOn)}
        {comparison !== undefined && (
          <>
            {' '}
            <TextAction
              onClick={() => setOpen((value) => !value)}
              testId={`${PREFIX}-change-see-${card.itemKey}`}
            >
              {open ? W.HIDE : W.SEE_WHAT_CHANGED}
            </TextAction>
          </>
        )}
      </p>
    ) : undefined;
  return (
    <ItemCard testId={`${PREFIX}-change-${card.itemKey}`}>
      <CardTitle title={card.title} mark={card.mark} />
      <AskedYou itemKeys={[card.itemKey]} />
      {card.kind === 'no_filing' ? (
        <>
          {card.line !== undefined && <p className="text-s13 leading-h15 text-text">{card.line}</p>}
          {card.evidence !== undefined && (
            <EvidenceNote line={card.evidence} testId={`${PREFIX}-evidence`} />
          )}
          {/* A rule updated as well (U5): what changed is read here; the choice stays 105's. */}
          {updated}
          {open && comparison !== undefined && (
            <WhatChanged
              comparison={comparison}
              layout="table"
              repinLine={false}
              testId={`${PREFIX}-change-compare-${card.itemKey}`}
            />
          )}
          {!viewer && (
            <div className="flex flex-col gap-8">
              <ActionRow>
                <ChoiceButtons
                  options={[
                    { value: 'keep_tracking' as const, label: W.KEEP_TRACKING },
                    { value: 'stop_tracking' as const, label: W.STOP_TRACKING },
                  ]}
                  value={
                    choice === 'keep_tracking' || choice === 'stop_tracking' ? choice : undefined
                  }
                  onChange={onChoose}
                  testId={`${PREFIX}-change-choice-${card.itemKey}`}
                />
                <AskAside itemKeys={[card.itemKey]} parts={askParts} />
              </ActionRow>
              {choice === 'stop_tracking' && (
                <p className="text-s12 leading-h15 text-text-tertiary">{W.STOP_TRACKING_LINE}</p>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {updated}
          {open && comparison !== undefined && (
            // N38's line only with its act (U6: someone who can only view would read it as done).
            <WhatChanged
              comparison={comparison}
              layout="table"
              repinLine={!viewer}
              testId={`${PREFIX}-change-compare-${card.itemKey}`}
            >
              {!viewer && (
                <ActionRow>
                  <ChoiceButtons
                    options={[
                      { value: 'use_updated' as const, label: W.USE_UPDATED_RULE },
                      { value: 'change_later' as const, label: W.LATER },
                    ]}
                    value={
                      choice === 'use_updated' || choice === 'change_later' ? choice : undefined
                    }
                    onChange={onChoose}
                    testId={`${PREFIX}-change-choice-${card.itemKey}`}
                  />
                  <AskAside itemKeys={[card.itemKey]} parts={askParts} />
                </ActionRow>
              )}
            </WhatChanged>
          )}
          {/* Closed (frame 102), or nothing approved words say to open: the ask under N36. */}
          {(viewer || !opened) && <AskAside itemKeys={[card.itemKey]} parts={askParts} />}
        </>
      )}
      {card.kind === 'no_filing' && viewer && (
        <AskAside itemKeys={[card.itemKey]} parts={askParts} />
      )}
    </ItemCard>
  );
}

function FilingsSection({
  view,
  choices,
  onChoose,
  viewer,
  nameOf,
  onAnswer,
  onOpen,
}: {
  view: ReviewView;
  choices: ReadonlyMap<string, Choice>;
  onChoose: (key: string, value: ChoiceValue | undefined, reason?: string) => void;
  viewer: boolean;
  nameOf: (userId: string) => string | undefined;
  onAnswer: (answer: Answer) => Promise<boolean>;
  onOpen: (obligationId: string) => void;
}) {
  // N47 (choice 1): on this section only, while two or more could be added. It marks each card
  // "Add to calendar" where it can be seen; each stays changeable; nothing is saved until Save.
  const cards = view.filings;
  // A stopped filing is never added back in bulk: starting it again is its own choice (D-CCM-58).
  const addable = cards.filter((card) => card.stopped === undefined);
  const allAdded =
    addable.length > 0 && addable.every((card) => choices.get(card.itemKey)?.value === 'add');
  const action =
    !viewer && addable.length >= 2 && !allAdded ? (
      <TextAction
        onClick={() => {
          for (const card of addable) onChoose(card.itemKey, 'add');
        }}
        testId={`${PREFIX}-add-all`}
      >
        {W.addAllLabel(addable.length)}
      </TextAction>
    ) : undefined;
  return (
    <ReviewSection title={W.SECTION_FILINGS} action={action} testId={`${PREFIX}-filings`}>
      {cards.map((card) => (
        <FilingItem
          key={card.itemKey}
          card={card}
          choice={choices.get(card.itemKey)}
          onChoose={(value, reason) => onChoose(card.itemKey, value, reason)}
          viewer={viewer}
          nameOf={nameOf}
          onAnswer={onAnswer}
        />
      ))}
      {/* N11: linked, never duplicated; a filing already on the calendar needs no choice here. */}
      {view.onCalendar.length > 0 && (
        <div className="flex flex-col gap-4 px-4" {...testIdProp(`${PREFIX}-on-calendar`)}>
          <p className="text-s13 font-w600 text-text-secondary">{W.ALREADY_ON_CALENDAR}</p>
          {view.onCalendar.map((line) => (
            <p key={line.itemKey} className="text-s13 leading-h15 text-text">
              {line.title}
              {line.addedBy !== undefined && (
                <span className="text-text-secondary">
                  {' · '}
                  {W.addedByAt(
                    nameIfKnown(nameOf, line.addedBy.decidedByUserId),
                    timeOf(line.addedBy.decidedAt),
                  )}
                </span>
              )}
              {' · '}
              <TextAction
                onClick={() => onOpen(line.obligationId)}
                testId={`${PREFIX}-open-${line.itemKey}`}
              >
                {W.OPEN_THE_FILING}
              </TextAction>
            </p>
          ))}
        </div>
      )}
    </ReviewSection>
  );
}

/** "Correct" on a fact, or a question's own field: the profile's own door (frame 92's control). */
function useCorrecting() {
  const [correcting, setCorrecting] = useState<FactLine | undefined>();
  return { correcting, setCorrecting };
}

function FilingItem({
  card,
  choice,
  onChoose,
  viewer,
  nameOf,
  onAnswer,
}: {
  card: FilingCard;
  choice: Choice | undefined;
  onChoose: (value: ChoiceValue | undefined, reason?: string) => void;
  viewer: boolean;
  nameOf: (userId: string) => string | undefined;
  onAnswer: (answer: Answer) => Promise<boolean>;
}) {
  const { correcting, setCorrecting } = useCorrecting();
  const askParts = useAskParts([card.itemKey], card.title);
  return (
    <ItemCard testId={`${PREFIX}-filing-${card.itemKey}`}>
      <CardTitle title={card.title} mark={card.mark} />
      <AskedYou itemKeys={[card.itemKey]} />
      {card.reopened !== undefined ? (
        <p className="text-s13 leading-h15 text-text" {...testIdProp(`${PREFIX}-reopened`)}>
          {W.chosenNotToFile(
            decidedDay(card.reopened.decision),
            nameIfKnown(nameOf, card.reopened.decision.decidedByUserId),
            card.reopened.cause,
          )}
          {card.reopened.decision.reason !== undefined && (
            <>
              <br />“{card.reopened.decision.reason}”
            </>
          )}
        </p>
      ) : card.stopped !== undefined ? (
        // S5 (D-CCM-58), in N35's place and voice, with N7 after it in its own lighter grey, as on
        // every other filing card (REVIEW-19's F5).
        <p className="text-s13 leading-h15 text-text" {...testIdProp(`${PREFIX}-stopped`)}>
          {card.stopped.stoppedChannel === 'connector'
            ? // D-CCM-96: a stop made through the person's connected AI, in N56's form.
              W.youStoppedTrackingThroughAi(
                displayLocalDate(card.stopped.stoppedAt),
                nameIfKnown(nameOf, card.stopped.stoppedByUserId),
              )
            : W.youStoppedTracking(
                displayLocalDate(card.stopped.stoppedAt),
                nameIfKnown(nameOf, card.stopped.stoppedByUserId),
              )}{' '}
          <span className="text-text-secondary">{W.MEETS_CONDITIONS}</span>
        </p>
      ) : (
        <p className="text-s13 leading-h15 text-text-secondary">{W.MEETS_CONDITIONS}</p>
      )}
      {card.openDetail !== undefined && (
        // W4 (MD-25K's G2): what the result's line leaves open, beside the result, as N24's note.
        <EvidenceNote line={card.openDetail} testId={`${PREFIX}-open-detail-${card.itemKey}`} />
      )}
      <DateBlock card={card.date} testId={`${PREFIX}-date`} />
      <FactList
        facts={card.facts}
        testId={`${PREFIX}-facts`}
        {...(!viewer ? { onCorrect: setCorrecting } : {})}
      />
      {correcting !== undefined && correcting.correctAt !== undefined && (
        <AnswerForm
          control={controlFor(correcting.correctAt)}
          onSave={async (value) => {
            if (await onAnswer(value)) setCorrecting(undefined);
          }}
          onCancel={() => setCorrecting(undefined)}
          testId={`${PREFIX}-correct-form`}
        />
      )}
      <BasisToggle basis={card.basis} testId={`${PREFIX}-basis-${card.itemKey}`} />
      {!viewer && (
        <div className="flex flex-col gap-10">
          <ActionRow>
            <ChoiceButtons
              options={[
                { value: 'add' as const, label: W.ADD_TO_CALENDAR },
                { value: 'not_filing' as const, label: W.NOT_FILING },
              ]}
              value={
                choice?.value === 'add' || choice?.value === 'not_filing' ? choice.value : undefined
              }
              onChange={(next) => onChoose(next)}
              testId={`${PREFIX}-choice-${card.itemKey}`}
            />
            <AskAside itemKeys={[card.itemKey]} parts={askParts} />
          </ActionRow>
          {choice?.value === 'not_filing' && (
            <div className="max-w-[420px]">
              <Field
                label={W.WHY_OPTIONAL}
                placeholder={W.WHY_PLACEHOLDER}
                value={choice.reason ?? ''}
                maxLength={500}
                onChange={(event) => onChoose('not_filing', event.target.value)}
                testId={`${PREFIX}-why-${card.itemKey}`}
              />
            </div>
          )}
        </div>
      )}
      {viewer && <AskAside itemKeys={[card.itemKey]} parts={askParts} />}
    </ItemCard>
  );
}

function NoFilingSection({
  view,
  choices,
  onChoose,
  viewer,
  calendarRules,
  onAddFiling,
  onAnswer,
}: {
  view: ReviewView;
  choices: ReadonlyMap<string, Choice>;
  onChoose: (key: string, value: ChoiceValue | undefined) => void;
  viewer: boolean;
  calendarRules: ReadonlySet<string>;
  onAddFiling: (card: ExclusionCard) => void;
  onAnswer: (answer: Answer) => Promise<boolean>;
}) {
  const [showFolded, setShowFolded] = useState(false);
  const { shown, folded } = view.noFiling;
  return (
    <ReviewSection title={W.SECTION_NO_FILING} testId={`${PREFIX}-no-filing`}>
      {shown.map((card) => (
        <ExclusionItem
          key={card.itemKey}
          card={card}
          choice={choices.get(card.itemKey)?.value}
          onChoose={(value) => onChoose(card.itemKey, value)}
          viewer={viewer}
          onTheCalendar={calendarRules.has(ruleKey(card.ref))}
          onAddFiling={() => onAddFiling(card)}
          onAnswer={onAnswer}
        />
      ))}
      {folded.length > 0 && (
        <div className="flex flex-col gap-8">
          <p className="text-s13 text-text-secondary">
            {W.moreNotMet(folded.length, shown.length > 0)}
            {' · '}
            <TextAction
              onClick={() => setShowFolded((current) => !current)}
              testId={`${PREFIX}-folded-toggle`}
            >
              {showFolded ? W.HIDE : W.SHOW}
            </TextAction>
          </p>
          {showFolded &&
            folded.map((card) => (
              <ExclusionItem
                key={card.itemKey}
                card={card}
                choice={choices.get(card.itemKey)?.value}
                onChoose={(value) => onChoose(card.itemKey, value)}
                viewer={viewer}
                onTheCalendar={calendarRules.has(ruleKey(card.ref))}
                onAddFiling={() => onAddFiling(card)}
                onAnswer={onAnswer}
              />
            ))}
        </div>
      )}
    </ReviewSection>
  );
}

function ExclusionItem({
  card,
  choice,
  onChoose,
  viewer,
  onTheCalendar,
  onAddFiling,
  onAnswer,
}: {
  card: ExclusionCard;
  choice: ChoiceValue | undefined;
  onChoose: (value: ChoiceValue | undefined) => void;
  viewer: boolean;
  onTheCalendar: boolean;
  onAddFiling: () => void;
  onAnswer: (answer: Answer) => Promise<boolean>;
}) {
  const { correcting, setCorrecting } = useCorrecting();
  const askParts = useAskParts([card.itemKey], card.title);
  return (
    <ItemCard testId={`${PREFIX}-exclusion-${card.itemKey}`}>
      <CardTitle title={card.title} mark={card.mark} />
      <AskedYou itemKeys={[card.itemKey]} />
      <p className="text-s13 leading-h15 text-text">{card.label}</p>
      {card.openDetail !== undefined && (
        // W4 (MD-25K's G2): what the result's line leaves open, beside the result, as N24's note.
        <EvidenceNote line={card.openDetail} testId={`${PREFIX}-open-detail-${card.itemKey}`} />
      )}
      {card.evidence !== undefined && (
        <EvidenceNote line={card.evidence} testId={`${PREFIX}-evidence`} />
      )}
      <FactList
        facts={card.facts}
        testId={`${PREFIX}-facts`}
        {...(!viewer ? { onCorrect: setCorrecting } : {})}
      />
      {correcting !== undefined && correcting.correctAt !== undefined && (
        <AnswerForm
          control={controlFor(correcting.correctAt)}
          onSave={async (value) => {
            if (await onAnswer(value)) setCorrecting(undefined);
          }}
          onCancel={() => setCorrecting(undefined)}
          testId={`${PREFIX}-correct-form`}
        />
      )}
      <BasisToggle basis={card.basis} testId={`${PREFIX}-basis-${card.itemKey}`} />
      {!viewer && (
        <ActionRow>
          <ChoiceButtons
            options={[
              { value: 'agree' as const, label: W.THATS_RIGHT },
              { value: 'we_file' as const, label: W.WE_FILE_IT },
            ]}
            value={choice === 'agree' || choice === 'we_file' ? choice : undefined}
            onChange={(next) => onChoose(next)}
            testId={`${PREFIX}-choice-${card.itemKey}`}
          />
          <AskAside itemKeys={[card.itemKey]} parts={askParts} />
        </ActionRow>
      )}
      {choice === 'we_file' && (
        <div className="flex flex-col items-start gap-8 rounded-lg bg-surface-sunken px-16 py-12">
          <p className="text-s13 leading-h15 text-text">{W.WE_FILE_IT_CHOSEN}</p>
          {!onTheCalendar && (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={onAddFiling}
              testId={`${PREFIX}-add-a-filing`}
            >
              {W.ADD_A_FILING}
            </Button>
          )}
        </div>
      )}
      {viewer && <AskAside itemKeys={[card.itemKey]} parts={askParts} />}
    </ItemCard>
  );
}

/** The answer control a profile field takes (frame 92's: states, month and day, yes or no). */
function controlFor(parameterKey: string): AnswerControl {
  if (parameterKey === 'solicitation_jurisdictions') return { kind: 'jurisdictions' };
  if (parameterKey === 'fiscal_year_end') return { kind: 'month_day' };
  return { kind: 'boolean', parameterKey };
}

/**
 * A question's own field, or "Correct" on a fact: the profile's door, with "Save" (D-CCM-21) and,
 * for a correction, "Cancel". The answer is recorded as the organization's fact; the review is then
 * re-presented with it.
 */
function AnswerForm({
  control,
  onSave,
  onCancel,
  label,
  disabled,
  testId,
}: {
  control: AnswerControl;
  onSave: (answer: Answer) => Promise<void>;
  onCancel?: () => void;
  label?: string;
  disabled?: boolean;
  testId: string;
}) {
  const recorded = useContext(RecordedStates);
  const facts = useContext(RecordedFactsContext);
  // Until the person changes it, the list is the one on record (read even if it arrives later).
  const [edited, setEdited] = useState<string[] | undefined>();
  const codes = edited ?? [...recorded];
  // S1 (D-CCM-72): the organization's statement that the list is complete, as recorded until changed.
  const [completeEdited, setCompleteEdited] = useState<boolean | undefined>();
  const complete = completeEdited ?? facts.statesComplete;
  const [monthDay, setMonthDay] = useState({ month: '', day: '' });
  const [yearEndChange, setYearEndChange] = useState<YearEndChange | undefined>();
  const [pending, setPending] = useState(false);
  if (control.kind === 'none') return null;
  const run = async (answer: Answer) => {
    setPending(true);
    try {
      await onSave(answer);
    } finally {
      setPending(false);
    }
  };
  const cancel =
    onCancel !== undefined ? (
      <Button
        appearance="saas"
        tone="ghost"
        size="sm"
        onClick={onCancel}
        disabled={pending}
        testId={`${testId}-cancel`}
      >
        {W.CANCEL}
      </Button>
    ) : null;
  if (control.kind === 'boolean') {
    return (
      <div className="flex flex-wrap items-center gap-8" {...testIdProp(testId)}>
        {[
          { label: W.YES, flag: true },
          { label: W.NO, flag: false },
        ].map((option) => (
          <Button
            key={option.label}
            appearance="saas"
            tone="secondary"
            size="sm"
            disabled={disabled || pending}
            onClick={() =>
              void run({ parameterKey: control.parameterKey, value: { flag_value: option.flag } })
            }
            testId={`${testId}-${option.flag ? 'yes' : 'no'}`}
          >
            {option.label}
          </Button>
        ))}
        {cancel}
      </div>
    );
  }
  // CCM-BE24-B (W1): a year end that moves under filings counting from it asks Y1 before Save.
  const earlierYearEnd =
    control.kind === 'month_day' &&
    facts.yearEndChangeRequired &&
    facts.yearEnd !== undefined &&
    monthDay.month !== '' &&
    monthDay.day !== '' &&
    (Number(monthDay.month) !== facts.yearEnd.month || Number(monthDay.day) !== facts.yearEnd.day)
      ? facts.yearEnd
      : undefined;
  const ready =
    control.kind === 'jurisdictions'
      ? // An empty list says something only when it is stated complete (S4).
        (codes.length > 0 || complete) &&
        (!sameMembers(codes, recorded) || complete !== facts.statesComplete)
      : monthDay.month !== '' &&
        monthDay.day !== '' &&
        (earlierYearEnd === undefined || yearEndChange !== undefined);
  const save = (
    <Button
      appearance="saas"
      tone="secondary"
      size="sm"
      disabled={!ready || disabled || pending}
      loading={pending}
      onClick={() =>
        void run(
          control.kind === 'jurisdictions'
            ? {
                parameterKey: 'solicitation_jurisdictions',
                value: {
                  jurisdiction_codes: codes.filter((code) => OFFERED_STATES.has(code)),
                  ...(facts.served ? { jurisdiction_list_complete: complete } : {}),
                },
              }
            : {
                parameterKey: 'fiscal_year_end',
                value: {
                  fiscal_year_end_month: Number(monthDay.month),
                  fiscal_year_end_day: Number(monthDay.day),
                  ...(earlierYearEnd !== undefined && yearEndChange !== undefined
                    ? { year_end_change: yearEndChange }
                    : {}),
                },
              },
        )
      }
      testId={`${testId}-save`}
    >
      {W.SAVE}
    </Button>
  );
  // Save sits on the field's baseline, bottom-aligned as frames 92 and 94 draw it
  // (CCM-R1-REVIEW-WEB-SAVE-OFFSET: centred, it rode 2 px high beside the taller field).
  const row = (
    <div className="flex flex-wrap items-end gap-8" {...testIdProp(testId)}>
      {control.kind === 'jurisdictions' ? (
        <StatesPicker
          codes={codes}
          onChange={setEdited}
          disabled={disabled || pending}
          label={label ?? W.CHOOSE_STATES}
          testId={`${testId}-states`}
        />
      ) : (
        <MonthDayPicker
          month={monthDay.month}
          day={monthDay.day}
          onChange={setMonthDay}
          disabled={disabled || pending}
          testId={`${testId}-month-day`}
        />
      )}
      {save}
      {cancel}
    </div>
  );
  if (control.kind === 'month_day') {
    // CCM-BE24-B (W1): the question under the month and day where moving the year end asks Y1, as
    // REVIEW-21 puts the states statement under its list; Save, beside the field, waits for it.
    // The wrapper stays put, so choosing a date never remounts the field under the hand.
    return (
      <div className="flex flex-col gap-10">
        {row}
        {earlierYearEnd !== undefined && (
          <YearEndChangeQuestion
            earlier={earlierYearEnd}
            chosen={{ month: Number(monthDay.month), day: Number(monthDay.day) }}
            organization={facts.organization}
            today={localIsoDate(new Date())}
            value={yearEndChange}
            onChange={setYearEndChange}
            disabled={disabled || pending}
            testId={`${testId}-year-end-change`}
          />
        )}
      </div>
    );
  }
  if (!facts.served) return row;
  // REVIEW-21: S1 under the list (S4 while no state is chosen), and what it does (S2).
  return (
    <div className="flex flex-col gap-10">
      {row}
      <label className="flex max-w-[560px] cursor-pointer items-start gap-8">
        <Checkbox
          appearance="saas"
          checked={complete}
          disabled={disabled || pending}
          onCheckedChange={(next) => setCompleteEdited(next === true)}
          testId={`${testId}-complete`}
        />
        <span className="flex flex-col gap-2">
          <span className="text-s14 leading-h15 text-text">
            {codes.length > 0
              ? W.allOurStates(facts.organization)
              : W.asksInNoState(facts.organization)}
          </span>
          {codes.length > 0 && (
            <span className="text-s13 leading-h15 text-text-secondary">
              {W.otherStatesRead(facts.organization)}
            </span>
          )}
        </span>
      </label>
    </div>
  );
}

function QuestionItem({
  card,
  choice,
  onChoose,
  viewer,
  nameOf,
  onAnswer,
  upload,
  aiOff,
  onUpload,
}: {
  card: QuestionCard;
  choice: ChoiceValue | undefined;
  onChoose: (value: ChoiceValue | undefined) => void;
  viewer: boolean;
  nameOf: (userId: string) => string | undefined;
  onAnswer: (answer: Answer) => Promise<boolean>;
  upload?: string;
  aiOff: boolean;
  onUpload: () => void;
}) {
  // N22's list: two filings in full, more as the first and "and {n} more" (frame 92).
  const decides =
    card.decides.length > 2
      ? `${card.decides[0]} and ${card.decides.length - 1} more`
      : card.decides.join(', ');
  const dontKnow = choice === 'dont_know';
  const askParts = useAskParts(card.itemKeys, card.title);
  if (card.kind === 'stale') {
    // S: Givmo's own record, past its freshness rule. Nothing the organization answers settles it,
    // so the card says what waits on it and offers no choice.
    return (
      <div
        className="flex flex-col gap-4 rounded-lg border border-border-subtle px-16 py-12"
        {...testIdProp(`${PREFIX}-question-${card.key}`)}
      >
        <p className="text-s13 font-w600 leading-h15 text-text">{card.title}</p>
        {card.door !== undefined && <p className="text-s13 leading-h15 text-text">{card.door}</p>}
        {decides.length > 0 && (
          <p className="text-s12 leading-h15 text-text-secondary">
            {W.leavesUnresolved(card.decides.join(', '))}
          </p>
        )}
      </div>
    );
  }
  return (
    <ItemCard testId={`${PREFIX}-question-${card.key}`}>
      <CardTitle title={card.title} mark={card.mark} />
      <AskedYou itemKeys={card.itemKeys} />
      {card.standing !== undefined && (
        <p className="text-s13 leading-h15 text-text-secondary">
          {W.dontKnowYet(
            nameIfKnown(nameOf, card.standing.decidedByUserId),
            decidedDay(card.standing),
          )}
        </p>
      )}
      {decides.length > 0 && (
        <p className="text-s12 leading-h15 text-text-tertiary">{W.yourAnswerDecides(decides)}</p>
      )}
      {card.door !== undefined && (
        <p className="text-s13 leading-h15 text-text-secondary">{card.door}</p>
      )}
      {card.quoted.length > 0 && (
        <div className="flex flex-col gap-4">
          <ul className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-subtle">
            {card.quoted.map((words) => (
              <li key={words} className="px-12 py-10 text-s13 leading-h15 text-text">
                “{words}”
              </li>
            ))}
          </ul>
          {card.citation !== undefined && (
            <p className="text-s12 leading-h15 text-text-tertiary">{card.citation}</p>
          )}
        </div>
      )}
      {card.example !== undefined && (
        <p
          className="text-s12 leading-h15 text-text-secondary"
          {...testIdProp(`${PREFIX}-example`)}
        >
          {card.example.lead} {card.example.quote}
          {card.example.citation !== undefined && (
            <span className="block text-text-tertiary">{card.example.citation}</span>
          )}
        </p>
      )}
      {card.fee !== undefined && (
        <FeeChart chart={card.fee.chart} caption={card.fee.caption} testId={`${PREFIX}-fee`} />
      )}
      {!viewer && card.control.kind !== 'none' && (
        <AnswerForm
          control={card.control}
          onSave={async (value) => {
            await onAnswer(value);
          }}
          label={card.title}
          disabled={dontKnow}
          testId={`${PREFIX}-answer-${card.key}`}
        />
      )}
      {upload !== undefined && (
        <p
          className="text-s12 leading-h15 text-text-tertiary"
          {...testIdProp(`${PREFIX}-uploaded`)}
        >
          {aiOff ? `${upload} · ${W.KEPT_NOT_READ_AI_OFF}` : W.uploadedNotRead(upload)}
        </p>
      )}
      {!viewer && (
        <QuietLine testId={`${PREFIX}-question-actions`}>
          {[
            dontKnow ? (
              <span key="dont-know" className="flex items-baseline gap-6">
                <span className="font-w600 text-text">{W.YOU_DONT_KNOW}</span>
                <TextAction onClick={() => onChoose(undefined)} testId={`${PREFIX}-dont-know-undo`}>
                  {W.UNDO}
                </TextAction>
              </span>
            ) : card.standing === undefined ? (
              <TextAction
                key="dont-know"
                onClick={() => onChoose('dont_know')}
                testId={`${PREFIX}-dont-know`}
              >
                {W.I_DONT_KNOW}
              </TextAction>
            ) : null,
            <TextAction key="upload" onClick={onUpload} testId={`${PREFIX}-upload`}>
              {W.UPLOAD_A_DOCUMENT}
            </TextAction>,
            ...askParts,
          ]}
        </QuietLine>
      )}
      {viewer && <AskAside itemKeys={card.itemKeys} parts={askParts} />}
    </ItemCard>
  );
}

function IssueItem({
  card,
  choice,
  onChoose,
  viewer,
}: {
  card: IssueCard;
  choice: ChoiceValue | undefined;
  onChoose: (value: ChoiceValue | undefined) => void;
  viewer: boolean;
}) {
  const askParts = useAskParts(card.itemKeys, card.title);
  return (
    <ItemCard testId={`${PREFIX}-issue-${card.key}`}>
      <CardTitle title={card.title} mark={card.mark} />
      <AskedYou itemKeys={card.itemKeys} />
      {card.reasonWords.map((words) => (
        <p key={words} className="text-s13 leading-h15 text-text">
          {words}
        </p>
      ))}
      {card.openDetailLead !== undefined && (
        // W4 (MD-25K's G2), leading the source's words it would quote, which follow.
        <p
          className="text-s13 leading-h15 text-text-secondary"
          {...testIdProp(`${PREFIX}-open-detail-${card.key}`)}
        >
          {card.openDetailLead}
        </p>
      )}
      {card.sourceWords.map((words, index) => {
        const citation = card.sourceCitations[index];
        return citation === undefined ? (
          <p key={words} className="text-s13 leading-h15 text-text">
            “{words}”
          </p>
        ) : (
          // J3 (cut 1008-122): each source's words with its citation beneath, as a question cites.
          <div key={words} className="flex flex-col gap-4">
            <p className="text-s13 leading-h15 text-text">“{words}”</p>
            <p className="text-s12 leading-h15 text-text-tertiary">{citation}</p>
          </div>
        );
      })}
      {card.blocks.length > 0 && (
        <p className="text-s13 leading-h15 text-text-secondary">
          {W.leavesUnresolved(card.blocks.join(', '))}
        </p>
      )}
      <FactList facts={card.facts} testId={`${PREFIX}-facts`} />
      <p className="text-s13 leading-h15 text-text-secondary">{W.FOR_ORG_OR_COUNSEL}</p>
      {!viewer && (
        <ActionRow>
          <ChoiceButtons
            options={[{ value: 'later' as const, label: W.DECIDE_LATER }]}
            value={choice === 'later' ? choice : undefined}
            onChange={(next) => onChoose(next)}
            testId={`${PREFIX}-later-${card.key}`}
          />
          <AskAside itemKeys={card.itemKeys} parts={askParts} />
        </ActionRow>
      )}
      {viewer && <AskAside itemKeys={card.itemKeys} parts={askParts} />}
    </ItemCard>
  );
}

function DecidedSection({
  cards,
  justSaved,
  nameOf,
  viewer,
  onChange,
  onAddFiling,
}: {
  cards: readonly DecidedCard[];
  justSaved: boolean;
  nameOf: (userId: string) => string | undefined;
  viewer: boolean;
  onChange: (card: DecidedCard) => void;
  onAddFiling: (card: DecidedCard) => void;
}) {
  const [open, setOpen] = useState(justSaved);
  useEffect(() => {
    if (justSaved) setOpen(true);
  }, [justSaved]);
  return (
    <section className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-decided`)}>
      <div className="flex items-baseline gap-8">
        <h2 className="text-s16 font-w600 text-text-strong">
          {justSaved ? W.DECIDED : W.DECIDED_EARLIER}
        </h2>
        <TextAction
          onClick={() => setOpen((current) => !current)}
          testId={`${PREFIX}-decided-toggle`}
        >
          {open ? W.HIDE : W.SHOW}
        </TextAction>
      </div>
      {open && (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-border-subtle">
            {cards.map((card) => {
              const label = decidedChoiceLabel(card.kind, card.decision.decision, card.changeKind);
              const day = decidedDay(card.decision);
              return (
                <li
                  key={card.itemKey}
                  className="flex items-baseline justify-between gap-12 px-20 py-12"
                  {...testIdProp(`${PREFIX}-decided-${card.itemKey}`)}
                >
                  <div className="flex min-w-0 flex-col gap-2">
                    <p className="text-s14 text-text">{card.title}</p>
                    <p className="text-s12 text-text-tertiary">
                      {card.decision.channel === 'connector'
                        ? W.decidedThroughAiLine(label, nameOf(card.decision.decidedByUserId), day)
                        : W.decidedLine(
                            label,
                            nameIfKnown(nameOf, card.decision.decidedByUserId),
                            day,
                          )}
                      {card.decision.reason !== undefined && ` · “${card.decision.reason}”`}
                    </p>
                  </div>
                  {!viewer && (
                    <div className="flex shrink-0 items-baseline gap-12">
                      {card.offerAddFiling && (
                        <TextAction
                          onClick={() => onAddFiling(card)}
                          testId={`${PREFIX}-decided-add-a-filing`}
                        >
                          {W.ADD_A_FILING}
                        </TextAction>
                      )}
                      <TextAction
                        onClick={() => onChange(card)}
                        testId={`${PREFIX}-decided-change`}
                      >
                        {W.CHANGE}
                      </TextAction>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </section>
  );
}

/**
 * U23 (D-CCM-60): last on the review, the open asks on items it doesn't show now (Givmo put them off
 * until something changes, or they no longer apply, or no card shows them), each "{item} · Asked
 * {Name} on {date} · Withdraw". Anyone but the asker reads who asked (U20 (b), the orchestrator's
 * Q2), and someone who can only view reads no Withdraw; the person asked reads U23's own line.
 */
function NotInReviewSection({
  rows,
  acts,
  viewerUserId,
  nameOf,
  onWithdraw,
}: {
  rows: readonly { ask: ReviewAsk; title: string }[];
  acts: boolean;
  viewerUserId?: string;
  nameOf: (userId: string) => string | undefined;
  onWithdraw: (ask: ReviewAsk) => void;
}) {
  return (
    <section className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-not-in-review`)}>
      <h2 className="text-s16 font-w600 text-text-strong">{W.NOT_IN_THIS_REVIEW}</h2>
      <ul className="flex flex-col gap-6">
        {rows.map(({ ask, title }) => (
          <li key={ask.askId} {...testIdProp(`${PREFIX}-not-in-review-${ask.askId}`)}>
            {viewerUserId !== undefined && ask.askedUserId === viewerUserId ? (
              <div className="flex flex-col gap-4">
                <p className="text-s13 leading-h15 text-text">
                  {askedYouAboutItemLine(ask, title, nameOf)}
                </p>
                {/* Its note, as frame 94b shows it (N-U23). */}
                {ask.note !== undefined && (
                  <p className="text-s13 leading-h15 text-text">“{ask.note}”</p>
                )}
              </div>
            ) : (
              <QuietLine>
                {[
                  <span key="item" className="text-text">
                    {title}
                  </span>,
                  <span key="asked">{askedLine(ask, viewerUserId, nameOf)}</span>,
                  acts && (
                    <TextAction
                      key="withdraw"
                      onClick={() => onWithdraw(ask)}
                      testId={`${PREFIX}-not-in-review-withdraw-${ask.askId}`}
                    >
                      {W.WITHDRAW}
                    </TextAction>
                  ),
                ]}
              </QuietLine>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function NotSupportedSection({
  count,
  titles,
  inPlacesTold,
}: {
  count: number;
  titles: readonly string[];
  inPlacesTold: readonly string[];
}) {
  const [showAll, setShowAll] = useState(false);
  return (
    <section className="flex flex-col gap-8" {...testIdProp(`${PREFIX}-not-supported`)}>
      <h2 className="text-s16 font-w600 text-text-strong">{W.SECTION_NOT_SUPPORTED}</h2>
      <p className="text-s13 leading-h15 text-text-secondary">{W.notEvaluatedLine(count)}</p>
      {inPlacesTold.length > 0 && !showAll && (
        <div className="flex flex-col gap-4">
          <p className="text-s13 text-text-secondary">{W.IN_PLACES_TOLD}</p>
          <ul className="flex flex-col gap-2">
            {inPlacesTold.map((title) => (
              <li key={title} className="text-s13 text-text">
                {title}
              </li>
            ))}
          </ul>
        </div>
      )}
      {showAll && (
        <ul className="flex flex-col gap-2" {...testIdProp(`${PREFIX}-not-supported-list`)}>
          {titles.map((title) => (
            <li key={title} className="text-s13 text-text">
              {title}
            </li>
          ))}
        </ul>
      )}
      {!showAll && titles.length > 0 && (
        <div>
          <TextAction onClick={() => setShowAll(true)} testId={`${PREFIX}-not-supported-all`}>
            {W.SHOW_ALL}
          </TextAction>
        </div>
      )}
    </section>
  );
}

// ── Your decisions ─────────────────────────────────────────────────────────────────────────────

function DecisionsCard({
  selection,
  saving,
  saved,
  refusal,
  kept,
  answered,
  viewer,
  onSave,
  onShowUpdated,
}: {
  selection: Selection | undefined;
  saving: boolean;
  saved?: { decided: number; open: number };
  refusal?: Refusal;
  kept: boolean;
  answered: boolean;
  viewer: boolean;
  onSave: () => void;
  onShowUpdated: () => void;
}) {
  const chosen = selection?.chosen ?? [];
  // The divider closes what is listed above it; with nothing above (someone who can only view, as
  // frame 107 has it) it would read as the title's underline, so it goes.
  const listedAbove =
    refusal !== undefined ||
    kept ||
    answered ||
    saved !== undefined ||
    chosen.length > 0 ||
    !viewer;
  return (
    <Card className="flex flex-col gap-16 px-20 py-20" testId={`${PREFIX}-decisions`}>
      <h2 className="text-s16 font-w600 text-text-strong">{W.YOUR_DECISIONS}</h2>
      {refusal?.kind === 'stale' && (
        <div
          className="flex flex-col items-start gap-8 rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12"
          {...testIdProp(`${PREFIX}-stale`)}
        >
          <p className="text-s13 font-w600 leading-h15 text-text-strong">{refusal.detail}</p>
          {staleReasonLines(refusal.reasons, refusal.savedBy).map((line) => (
            <p key={line} className="text-s13 leading-h15 text-text-secondary">
              {line}
            </p>
          ))}
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onShowUpdated}
            testId={`${PREFIX}-show-updated`}
          >
            {W.SHOW_UPDATED}
          </Button>
        </div>
      )}
      {refusal?.kind === 'refused' && (
        <div
          className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12"
          {...testIdProp(`${PREFIX}-refused`)}
        >
          {refusal.title !== undefined && (
            <p className="text-s13 font-w600 leading-h15 text-text-strong">{refusal.title}</p>
          )}
          <p className="text-s13 leading-h15 text-text-secondary">{refusal.detail}</p>
        </div>
      )}
      {kept && (
        <p className="text-s13 leading-h15 text-text-secondary" {...testIdProp(`${PREFIX}-kept`)}>
          {W.CHOICES_KEPT}
        </p>
      )}
      {answered && (
        <p
          className="text-s13 leading-h15 text-text-secondary"
          {...testIdProp(`${PREFIX}-answered`)}
        >
          {W.ANSWER_SAVED}
        </p>
      )}
      {saved !== undefined && chosen.length === 0 && (
        <p className="text-s13 leading-h15 text-text" {...testIdProp(`${PREFIX}-saved`)}>
          {W.savedLine(saved.decided, saved.open)}
        </p>
      )}
      {chosen.length === 0 ? (
        // N28 asks for a choice, so someone who can only view never reads it (frame 107: N46 alone).
        saved === undefined &&
        !viewer && <p className="text-s13 leading-h15 text-text-secondary">{W.NOTHING_CHOSEN}</p>
      ) : (
        <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-chosen`)}>
          {CHOICE_LABELS.filter((entry) => chosen.some((item) => item.value === entry.value)).map(
            (entry) => {
              const items = chosen.filter((item) => item.value === entry.value);
              return (
                <div key={entry.value} className="flex flex-col gap-2">
                  <p className="flex items-baseline justify-between gap-8 text-s13 font-w600 leading-h15 text-text-strong">
                    <span>{entry.label}</span>
                    <span className="tabular-nums text-text-secondary">{items.length}</span>
                  </p>
                  <ul className="flex flex-col gap-2">
                    {items.map((item) => (
                      <li key={item.key} className="text-s13 leading-h15 text-text-secondary">
                        {item.title}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            },
          )}
        </div>
      )}
      <div
        className={cn('flex flex-col gap-10', listedAbove && 'border-t border-border-subtle pt-16')}
        {...testIdProp(`${PREFIX}-decisions-foot`)}
      >
        <ComplianceDisclosure surface="review" />
        <p className="text-s13 leading-h15 text-text-secondary">{W.ADDING_DOESNT_FILE}</p>
        {viewer ? (
          <p
            className="text-s13 leading-h15 text-text-secondary"
            {...testIdProp(`${PREFIX}-viewer`)}
          >
            {W.VIEWER_CANT_SAVE}
          </p>
        ) : (
          <Button
            appearance="saas"
            tone="primary"
            size="lg"
            className="w-full"
            disabled={chosen.length === 0 || refusal?.kind === 'stale'}
            loading={saving}
            onClick={onSave}
            testId={`${PREFIX}-save`}
          >
            {W.SAVE_DECISIONS}
          </Button>
        )}
      </div>
    </Card>
  );
}
